import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { storeService } from '../services/storeService';
import { StoreCatalogItem, StoreCategory, StoreProductDetail, StoreVariant, StoreVariantColor, PaymentMethod, PaymentMethodLabel } from '../models';
import { PaymentMethodIcon } from '../components/ui/PaymentMethodIcon';
import { useCartStore } from '../store/cartStore';
import { Spinner } from '../components/ui/Spinner';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/ui/ProductCard';
import { WhatsAppIcon } from '../components/ui/ContactIcons';
import { Modal } from '../components/ui/Modal';
import { HeartIcon } from '../components/ui/HeartIcon';
import { AlertTriangleIcon, CheckIcon } from '../components/ui/Icons';
import { productPlaceholderIcon } from '../utils/productPlaceholder';
import { ProductReviews } from '../components/product/ProductReviews';
import { useFavoriteToggle } from '../hooks/useFavoriteToggle';
import { usePaymentSettings } from '../hooks/usePaymentSettings';
import { formatCurrency } from '../utils/currency';
import { productImageSrcSet, safeImageUrl, thumbnailUrlOf } from '../utils/url';
import { getPixPrice, getInstallmentInfo } from '../utils/pricing';
import { buildProductInquiryWhatsAppUrl } from '../utils/whatsapp';
import { WHATSAPP_NUMBER } from '../constants/contact';
import { AGE_VARIATION_TYPE, extractAvailableValues, getVariationLabel } from '../utils/variantResolution';
import { useProductBabySize } from '../hooks/useProductBabySize';
import { PROMOTION_CAMPAIGN_ENABLED } from '../config/promotionCampaign';
import { trackViewContent } from '../utils/metaPixel';
import { StoreEventType, trackStoreEvent } from '../services/trackingService';
import { QuantityStepper } from '../components/ui/QuantityStepper';
import { ColorSwatchButton } from '../components/product/ColorSwatchButton';
import { SizeChipButton } from '../components/product/SizeChipButton';
import { useStickyCtaOffset } from '../hooks/useStickyCtaOffset';
import { ProductLightbox } from '../components/product/ProductLightbox';
import { pickDefaultColor } from '../hooks/useVariantSelection';
import { useToastStore } from '../store/toastStore';

function ChevronIcon() {
  return (
    <svg
      className="w-4 h-4 text-text-medium transition-transform duration-200 group-open:rotate-180"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

const ADDED_FEEDBACK_MS = 4000;
const MAIN_IMAGE_SIZES = '(min-width: 768px) 50vw, 100vw';

const GENDER_LABELS: Record<number, string> = {
  1: 'Meninos',
  2: 'Meninas',
  3: 'Outros',
  4: 'Unissex',
};

interface AgeSizeGuideRow {
  age: string;
  height: string;
  weight: string;
}

const AGE_SIZE_GUIDE: AgeSizeGuideRow[] = [
  { age: '2 anos', height: '85 - 92 cm', weight: '12 - 14 kg' },
  { age: '4 anos', height: '98 - 104 cm', weight: '15 - 17 kg' },
  { age: '6 anos', height: '110 - 116 cm', weight: '19 - 22 kg' },
  { age: '8 anos', height: '122 - 128 cm', weight: '24 - 28 kg' },
  { age: '10 anos', height: '134 - 140 cm', weight: '29 - 34 kg' },
  { age: '12 anos', height: '146 - 152 cm', weight: '36 - 42 kg' },
  { age: '14 anos', height: '158 - 164 cm', weight: '45 - 52 kg' },
];

function extractUniqueColors(variants: StoreVariant[]): StoreVariantColor[] {
  const colors: StoreVariantColor[] = [];
  const seen = new Set<number>();
  for (const v of variants) {
    if (v.color && !seen.has(v.color.id)) {
      seen.add(v.color.id);
      colors.push(v.color);
    }
  }
  return colors;
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const addItem = useCartStore((s) => s.addItem);
  const showToast = useToastStore((s) => s.show);
  const { isFavorited, handleToggle } = useFavoriteToggle();
  const { settings: paymentSettings } = usePaymentSettings();

  const [product, setProduct] = useState<StoreProductDetail | null>(null);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState('');
  const stickyCtaRef = useStickyCtaOffset<HTMLDivElement>();
  const [selectedColor, setSelectedColor] = useState<StoreVariantColor | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const addedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const loadedIdRef = useRef<number | null>(null);
  const [showSelectionHint, setShowSelectionHint] = useState(false);
  const [related, setRelated] = useState<StoreCatalogItem[]>([]);
  const selectionRef = useRef<HTMLDivElement>(null);
  const babyPreselectedForRef = useRef<number | null>(null);
  const { badgeByValue: babySizeBadges, recommendedValues: babyRecommendedValues } = useProductBabySize(product);

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);

  useEffect(() => () => clearTimeout(addedTimerRef.current), []);

  useEffect(() => {
    storeService.getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    const numId = Number(id);

    // Evita dupla execução em StrictMode: se já carregou este ID, pula
    if (loadedIdRef.current === numId) return;

    loadedIdRef.current = numId;
    const colorParam = searchParams.get('color');
    const varParam = searchParams.get('var');
    const colorIdParam = colorParam !== null ? Number(colorParam) : undefined;
    setLoading(true);
    storeService
      .getProduct(numId)
      .then((rawData) => {
        const data: StoreProductDetail = {
          ...rawData,
          images: [...rawData.images].sort(
            (a, b) => Number(b.isMain) - Number(a.isMain) || a.order - b.order
          ),
        };
        setProduct(data);
        setMainImage((data.images.find((img) => img.isMain) ?? data.images[0])?.imageUrl ?? '');

        trackStoreEvent({ type: StoreEventType.ProductView, productId: data.id });

        if (data.hasPrice && data.price !== null) {
          trackViewContent({
            id: data.id,
            sku: data.sku,
            name: data.name,
            price: data.promotionalPrice ?? data.price,
          });
        }

        const colors = extractUniqueColors(data.variants);
        let matchedColor: StoreVariantColor | undefined;

        if (colorIdParam !== undefined) {
          matchedColor = colors.find((c) => c.id === colorIdParam);
        } else if (varParam !== null) {
          const variantsWithValue = data.variants.filter((v) => v.variationValue === varParam);
          if (variantsWithValue.length > 0) {
            const variantWithStock = variantsWithValue.find((v) => v.stockQuantity > 0) ?? variantsWithValue[0];
            const variantColor = variantWithStock.color;
            if (variantColor) {
              matchedColor = colors.find((c) => c.id === variantColor.id);
            }
          }
        }

        if (matchedColor) {
          setSelectedColor(matchedColor);
        } else {
          setSelectedColor(pickDefaultColor(data));
        }
      })
      .finally(() => setLoading(false));
  }, [id, searchParams]);

  useEffect(() => {
    if (!product || !selectedColor) return;
    const colorImage = product.images.find((img) => img.colorId === selectedColor.id);
    if (colorImage) {
      setMainImage(colorImage.imageUrl);
    }
  }, [product, selectedColor]);

  useEffect(() => {
    if (!product || babyPreselectedForRef.current === product.id || babyRecommendedValues.length === 0) return;
    babyPreselectedForRef.current = product.id;
    if (selectedValue !== null) return;
    const available = extractAvailableValues(product.variants, selectedColor);
    const inStock = (value: string) =>
      product.variants.some(
        (v) =>
          v.variationValue === value &&
          v.stockQuantity > 0 &&
          (!selectedColor || v.color?.id === selectedColor.id)
      );
    const preferred = babyRecommendedValues.find((value) => available.includes(value) && inStock(value));
    if (preferred) setSelectedValue(preferred);
  }, [product, selectedColor, selectedValue, babyRecommendedValues]);

  useEffect(() => {
    if (!product) return;
    let active = true;
    storeService
      .getAlsoBought(product.id)
      .then((items) => {
        if (active) setRelated(items);
      })
      .catch(() => {
        if (active) setRelated([]);
      });
    return () => {
      active = false;
    };
  }, [product]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (!product) return <div className="text-center py-20 text-text-light">Produto não encontrado.</div>;

  const mainImageVersions = product.images.find((img) => img.imageUrl === mainImage);

  const categoryId = categories.find((c) => c.name === product.categoryName)?.id;

  const hasVariants = product.variants.length > 0;
  const hasColors = product.variants.some((v) => v.color !== null);
  const hasAgeVariation = product.variants.some((v) => v.variationTypeId === AGE_VARIATION_TYPE);

  // Unique colors across all variants
  const uniqueColors: StoreVariantColor[] = hasColors ? extractUniqueColors(product.variants) : [];

  // Unique variation values available for the selected color (all colors if none selected yet)
  const uniqueValues: string[] = [];
  {
    const seen = new Set<string>();
    for (const v of product.variants) {
      if (selectedColor && v.color?.id !== selectedColor.id) continue;
      if (!seen.has(v.variationValue)) {
        seen.add(v.variationValue);
        uniqueValues.push(v.variationValue);
      }
    }
  }

  // Resolve the actual variant from selected color + value
  const resolvedVariant: StoreVariant | null = (() => {
    if (!hasVariants) return null;
    if (hasColors && selectedColor && selectedValue) {
      return product.variants.find(
        (v) => v.variationValue === selectedValue && v.color?.id === selectedColor.id
      ) ?? null;
    }
    if (!hasColors && selectedValue) {
      return product.variants.find((v) => v.variationValue === selectedValue) ?? null;
    }
    if (hasColors && !selectedColor && uniqueColors.length === 0 && selectedValue) {
      return product.variants.find((v) => v.variationValue === selectedValue) ?? null;
    }
    return null;
  })();

  const stockQuantity = resolvedVariant?.stockQuantity ?? 0;
  const isOutOfStock = hasVariants && resolvedVariant !== null && stockQuantity === 0;
  const hasPrice = product.hasPrice && product.price !== null;
  const needsSelection = hasVariants && resolvedVariant === null;
  const variationLabel = getVariationLabel(product.variants[0]?.variationTypeId);
  const selectionPrompt =
    hasColors && !selectedColor ? 'Selecionar cor' : `Selecionar ${variationLabel.toLowerCase()}`;
  const hasSoldOutValue = uniqueValues.some((val) =>
    product.variants.some(
      (v) => v.variationValue === val && v.stockQuantity === 0 && (!selectedColor || v.color?.id === selectedColor.id),
    ),
  );

  const handleIncreaseQuantity = () => {
    if (quantity < stockQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handlePrimaryAction = () => {
    if (needsSelection) {
      setShowSelectionHint(true);
      selectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    handleAddToCart();
  };

  const handleNotifyOnWhatsApp = () => {
    const color = selectedColor ? `, cor ${selectedColor.name}` : '';
    const text = `Olá! Quero ser avisada quando o ${product.name}${color} chegar em outros tamanhos.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const handleAddToCart = () => {
    if (!product || product.price === null) return;
    const result = addItem({
      productId: product.id,
      variantId: resolvedVariant?.id ?? 0,
      productName: product.name,
      variantLabel: resolvedVariant?.label ?? '',
      variationTypeName: resolvedVariant?.variationTypeName,
      variantSku: resolvedVariant?.sku,
      colorName: selectedColor?.name,
      price: product.price,
      promotionalPrice: product.promotionalPrice,
      imageUrl: thumbnailUrlOf(mainImageVersions) || mainImage,
      quantity: quantity,
      stockQuantity: resolvedVariant?.stockQuantity ?? 0,
    });
    if (result === 'max_stock') {
      showToast('Você já tem na sacola toda a quantidade disponível em estoque.', 'error');
      return;
    }
    setQuantity(1);
    setAddedToCart(true);
    clearTimeout(addedTimerRef.current);
    addedTimerRef.current = setTimeout(() => setAddedToCart(false), ADDED_FEEDBACK_MS);
  };

  const displayPrice = product.promotionalPrice ?? product.price;
  const pixPrice =
    hasPrice && paymentSettings && !product.promotionalPrice
      ? getPixPrice(displayPrice as number, paymentSettings.pixDiscountPercentage)
      : null;
  const installmentInfo =
    hasPrice && paymentSettings && !product.promotionalPrice
      ? getInstallmentInfo(displayPrice as number, paymentSettings)
      : null;

  const handleAskOnWhatsApp = () => {
    if (!product) return;
    const variantLabel = resolvedVariant?.label ?? selectedValue ?? null;
    const url = buildProductInquiryWhatsAppUrl(product, displayPrice, selectedColor, variantLabel);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const purchaseButton = (
    <Button
      fullWidth
      size="lg"
      variant="primary"
      onClick={handlePrimaryAction}
      disabled={isOutOfStock || !hasPrice}
    >
      {isOutOfStock ? (
        'Esgotado'
      ) : needsSelection ? (
        selectionPrompt
      ) : (
        'Adicionar à sacola'
      )}
    </Button>
  );

  const viewBagLinkClass =
    'inline-flex items-center justify-center gap-2 w-full min-h-[48px] rounded-lg bg-chocolate-dark hover:bg-chocolate-deep text-white font-semibold text-base transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate focus-visible:ring-offset-2';

  const addedConfirmation = (
    <Link to="/cart" className={viewBagLinkClass}>
      <CheckIcon className="w-4 h-4" />
      Adicionado · Ver sacola
    </Link>
  );

  const askAboutProductButton = (
    <button
      type="button"
      onClick={handleAskOnWhatsApp}
      className="inline-flex items-start gap-1.5 min-h-[44px] py-2.5 text-left text-sm font-semibold text-chocolate-dark hover:text-chocolate-deep transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate rounded"
    >
      <span className="mt-0.5 shrink-0"><WhatsAppIcon /></span>
      Tirar dúvida sobre essa peça
    </button>
  );

  return (
    <div className="max-w-4xl lg:max-w-5xl mx-auto px-4 pt-2 pb-[calc(7rem+var(--consent-offset,0px)+env(safe-area-inset-bottom,0px))] md:pt-8 md:pb-8">
      <p role="status" className="sr-only">
        {addedToCart ? 'Produto adicionado à sacola.' : ''}
      </p>
      <button
        type="button"
        onClick={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/catalogo'))}
        className="group inline-flex items-center gap-2.5 min-h-[44px] pl-1.5 pr-4 mb-3 md:mb-5 rounded-full bg-white border border-champagne-dark text-sm font-semibold text-text-dark shadow-[0_2px_6px_-3px_rgba(60,36,21,0.18)] hover:border-chocolate-light hover:text-chocolate-dark active:bg-champagne-light transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate focus-visible:ring-offset-2"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-champagne text-chocolate-dark transition-transform duration-200 ease-out group-hover:-translate-x-0.5" aria-hidden="true">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </span>
        Voltar
      </button>
      <nav aria-label="Breadcrumb" className="hidden sm:flex items-center flex-wrap gap-1.5 text-xs text-text-medium mb-4">
        <Link to="/catalogo" className="hover:text-chocolate-dark transition-colors">
          Catálogo
        </Link>
        <span aria-hidden="true">/</span>
        {categoryId !== undefined ? (
          <Link to={`/catalogo?cat=${categoryId}`} className="hover:text-chocolate-dark transition-colors">
            {product.categoryName}
          </Link>
        ) : (
          <span>{product.categoryName}</span>
        )}
        <span aria-hidden="true">/</span>
        <span className="text-text-dark font-medium truncate max-w-[60vw] sm:max-w-xs" aria-current="page">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Imagens */}
        <div className="flex flex-col gap-3 min-w-0 md:sticky md:top-24 md:self-start">
          <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-champagne-light group cursor-zoom-in min-w-0 shadow-sm" onClick={openLightbox}>
            {mainImage ? (
              <img
                src={safeImageUrl(mainImage)}
                srcSet={productImageSrcSet(mainImageVersions)}
                sizes={MAIN_IMAGE_SIZES}
                alt={product.name}
                width={1600}
                height={1600}
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img src={productPlaceholderIcon(product.id)} alt="" className="w-28 h-28 opacity-70" />
              </div>
            )}
            <button
              type="button"
              aria-label="Ampliar imagem"
              className="absolute top-2 right-2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
            {product.images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
                    const prevIdx = currentIdx <= 0 ? product.images.length - 1 : currentIdx - 1;
                    setMainImage(product.images[prevIdx].imageUrl);
                  }}
                  aria-label="Imagem anterior"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
                    const nextIdx = currentIdx >= product.images.length - 1 ? 0 : currentIdx + 1;
                    setMainImage(product.images[nextIdx].imageUrl);
                  }}
                  aria-label="Próxima imagem"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {product.images.map((img) => (
                    <span
                      key={img.id}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        img.imageUrl === mainImage ? 'bg-chocolate' : 'bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  aria-label={`Ver imagem ${product.images.indexOf(img) + 1} de ${product.images.length}`}
                  aria-current={mainImage === img.imageUrl}
                  onClick={() => setMainImage(img.imageUrl)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    mainImage === img.imageUrl ? 'border-chocolate' : 'border-transparent'
                  }`}
                >
                  <img src={safeImageUrl(thumbnailUrlOf(img))} alt="" width={64} height={64} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col gap-4 bg-white rounded-2xl shadow-sm p-5 sm:p-6 md:self-start">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-text-dark leading-tight text-balance">{product.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {GENDER_LABELS[product.gender] && (
                  <span className="px-2.5 py-0.5 rounded-full bg-champagne border border-champagne-dark text-xs font-semibold text-text-dark">
                    {GENDER_LABELS[product.gender]}
                  </span>
                )}
                {product.sku && <span className="hidden md:inline text-xs text-text-medium font-mono">SKU: {product.sku}</span>}
              </div>
            </div>
            {!PROMOTION_CAMPAIGN_ENABLED && (
              <button
                type="button"
                onClick={(e) => handleToggle(product.id, e)}
                aria-pressed={isFavorited(product.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border text-sm font-medium transition-colors ${
                  isFavorited(product.id)
                    ? 'bg-champagne border-chocolate text-chocolate-dark hover:bg-champagne-dark'
                    : 'bg-white border-champagne-dark text-text-medium hover:border-chocolate hover:text-chocolate'
                }`}
              >
                <HeartIcon filled={isFavorited(product.id)} className="w-4 h-4" />
                {isFavorited(product.id) ? 'Favoritado' : 'Favoritar'}
              </button>
            )}
          </div>

          {hasPrice ? (
            <div className="flex items-baseline gap-2">
              {product.promotionalPrice && (
                <span className="text-text-light line-through text-sm">R$ {formatCurrency(product.price as number)}</span>
              )}
              <span className="text-3xl font-extrabold text-chocolate">R$ {formatCurrency(displayPrice as number)}</span>
              {product.promotionalPrice && (
                <span className="self-center bg-text-dark text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
                  -{Math.round((1 - product.promotionalPrice / (product.price as number)) * 100)}%
                </span>
              )}
            </div>
          ) : (
            <p className="text-lg font-semibold text-text-medium">Consulte-nos para saber o preço</p>
          )}

          {hasPrice && pixPrice !== null && (
            <p className="self-start -mt-2 px-3 py-1 rounded-full bg-olivine-light text-text-dark text-sm font-semibold">
              R$ {formatCurrency(pixPrice)} no Pix
            </p>
          )}
          {hasPrice && installmentInfo && (
            <p className="text-sm text-text-medium -mt-2">
              ou {installmentInfo.count}x de R$ {formatCurrency(installmentInfo.amount)}
              {paymentSettings?.installmentsWithoutInterest && ' sem juros'}
            </p>
          )}

          {/* Seleção de cor */}
          {uniqueColors.length > 0 && (
            <div>
              <p className="text-sm font-medium text-text-medium mb-2">
                Cor{selectedColor && <span className="text-text-medium font-medium"> — {selectedColor.name}</span>}
              </p>
              <div className="flex flex-wrap gap-x-1 gap-y-0.5 -ml-1">
                {uniqueColors.map((color) => {
                  const outOfStock = !product.variants.some(
                    (v) => v.stockQuantity > 0 && v.color?.id === color.id
                  );
                  return (
                    <ColorSwatchButton
                      key={color.id}
                      color={color}
                      outOfStock={outOfStock}
                      selected={selectedColor?.id === color.id}
                      onSelect={() => {
                        setSelectedColor(selectedColor?.id === color.id ? null : color);
                        setSelectedValue(null);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Seleção de variação (tamanho / idade / etc) */}
          {hasVariants && uniqueValues.length > 0 && (
            <div
              ref={selectionRef}
              className={`rounded-xl transition-shadow ${showSelectionHint && needsSelection ? 'ring-2 ring-danger/40 p-2 -m-2' : ''}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <p className="text-sm font-medium text-text-medium">{variationLabel}</p>
                {hasAgeVariation && (
                  <button
                    type="button"
                    onClick={() => setSizeGuideOpen(true)}
                    className="inline-flex items-center min-h-[44px] text-sm text-chocolate-dark font-medium underline underline-offset-2 hover:text-chocolate transition-colors"
                  >
                    Ver guia de tamanhos
                  </button>
                )}
              </div>
              <div className={`flex flex-wrap gap-x-2 ${babySizeBadges.size > 0 ? 'gap-y-4 pt-2' : 'gap-y-2'}`}>
                {uniqueValues.map((val) => {
                  const variant = product.variants.find(
                    (v) => v.variationValue === val && (!selectedColor || v.color?.id === selectedColor.id)
                  );
                  const outOfStock = variant ? variant.stockQuantity === 0 : false;
                  return (
                    <SizeChipButton
                      key={val}
                      label={variant?.label ?? val}
                      soldOut={outOfStock}
                      selected={selectedValue === val}
                      badge={babySizeBadges.get(val)}
                      onSelect={() => setSelectedValue(selectedValue === val ? null : val)}
                    />
                  );
                })}
              </div>
              {showSelectionHint && needsSelection && (
                <p role="alert" className="mt-2 text-sm font-semibold text-danger">
                  Escolha {variationLabel.toLowerCase() === 'tamanho' ? 'um tamanho' : `uma opção de ${variationLabel.toLowerCase()}`} para continuar.
                </p>
              )}
              <div className="mt-2 flex flex-col items-start">
                {hasSoldOutValue && (
                  <button
                    type="button"
                    onClick={handleNotifyOnWhatsApp}
                    className="inline-flex items-start gap-1.5 min-h-[44px] py-2.5 text-left text-sm font-semibold text-chocolate-dark hover:text-chocolate-deep transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate rounded"
                  >
                    <span className="mt-0.5 shrink-0"><WhatsAppIcon /></span>
                    Não achou seu tamanho? Avisar quando chegar
                  </button>
                )}
                {askAboutProductButton}
              </div>
            </div>
          )}

          {/* Nota de estoque */}
          {hasVariants && resolvedVariant && (
            stockQuantity === 1 ? (
              <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger">
                <AlertTriangleIcon className="w-3.5 h-3.5" />
                Apenas 1 unidade disponível
              </p>
            ) : stockQuantity > 1 && stockQuantity <= 3 ? (
              <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger">
                <AlertTriangleIcon className="w-3.5 h-3.5" />
                Últimas {stockQuantity} unidades
              </p>
            ) : (
              <p className="text-xs text-text-medium">Disponível: {stockQuantity}</p>
            )
          )}

          {/* Seleção de quantidade */}
          {hasVariants && resolvedVariant && (
            <QuantityStepper
              quantity={quantity}
              onIncrease={handleIncreaseQuantity}
              onDecrease={handleDecreaseQuantity}
              disableDecrease={quantity <= 1}
              disableIncrease={quantity >= stockQuantity}
              size="lg"
              className="w-fit"
            />
          )}

          {hasPrice && <div className="hidden md:block">{addedToCart ? addedConfirmation : purchaseButton}</div>}
          {!(hasVariants && uniqueValues.length > 0) && <div className="self-start">{askAboutProductButton}</div>}
          <p className="text-sm text-text-medium">Enviamos para todo o Brasil. Troca em até 7 dias corridos.</p>

          <div className="border-t border-champagne-dark">
            {product.description && (
              <details className="group border-b border-champagne-dark" open>
                <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                  Descrição
                  <ChevronIcon />
                </summary>
                <div className="pb-4 text-base text-text-medium leading-relaxed">
                  <p>{product.description}</p>
                </div>
              </details>
            )}

            {product.compositionCare && (
              <details className="group border-b border-champagne-dark">
                <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                  Composição e cuidados
                  <ChevronIcon />
                </summary>
                <div className="pb-4 text-base text-text-medium leading-relaxed">
                  <p>{product.compositionCare}</p>
                </div>
              </details>
            )}

            <details className="group border-b border-champagne-dark">
              <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                Meios de pagamento
                <ChevronIcon />
              </summary>
              <div className="pb-4 flex flex-col gap-3">
                {[PaymentMethod.Pix, PaymentMethod.CreditCard, PaymentMethod.DebitCard, PaymentMethod.Cash].map((method) => (
                  <div key={method} className="flex items-center gap-2.5 text-base text-text-medium">
                    <span className="w-8 h-8 rounded-lg bg-champagne flex items-center justify-center text-chocolate-dark shrink-0">
                      <PaymentMethodIcon method={method} />
                    </span>
                    <span className="flex flex-col leading-tight">
                      <span className="font-semibold text-text-dark">{PaymentMethodLabel[method]}</span>
                      {method === PaymentMethod.Pix && hasPrice && pixPrice !== null && (
                        <span className="text-sm text-olivine-dark font-semibold">
                          R$ {formatCurrency(pixPrice)} à vista
                        </span>
                      )}
                      {method === PaymentMethod.CreditCard && hasPrice && installmentInfo && (
                        <span className="text-sm text-text-medium">
                          em até {installmentInfo.count}x de R$ {formatCurrency(installmentInfo.amount)}
                          {paymentSettings?.installmentsWithoutInterest && ' sem juros'}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </details>

            <details className="group border-b border-champagne-dark">
              <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                Entrega
                <ChevronIcon />
              </summary>
              <div className="pb-4 text-base text-text-medium leading-relaxed">
                <p>
                  Enviamos para todo o Brasil. O valor e o prazo variam de acordo com a sua região — combinamos
                  o frete diretamente pelo WhatsApp depois que você finaliza o pedido.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 min-h-[44px] font-semibold text-chocolate-dark hover:text-chocolate transition-colors mt-1"
                >
                  Falar no WhatsApp
                </a>
              </div>
            </details>

            <details className="group border-b border-champagne-dark">
              <summary className="flex items-center justify-between py-3 cursor-pointer select-none list-none font-semibold text-text-dark text-lg">
                Trocas e devoluções
                <ChevronIcon />
              </summary>
              <div className="pb-4 text-base text-text-medium leading-relaxed">
                <ol className="space-y-2 list-decimal list-inside">
                  <li>As peças devem estar sem sinais de uso, lavagem ou alteração, com etiqueta e lacre originais intactos.</li>
                  <li>Não realizamos trocas de peças promocionais ou adquiridas em liquidação.</li>
                  <li>Não realizamos trocas de peças de coleções sazonais.</li>
                  <li>Prazo para a troca: 7 dias corridos.</li>
                  <li>A troca da peça pode ser realizada uma vez.</li>
                  <li>Não trocamos peças íntimas.</li>
                  <li>A troca será permitida apenas por outro modelo da mesma coleção ou por tamanho diferente da mesma peça, conforme disponibilidade em estoque.</li>
                </ol>
              </div>
            </details>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-12" aria-labelledby="related-heading">
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <h2 id="related-heading" className="text-xl md:text-2xl font-display font-semibold text-text-dark text-balance">
              Quem comprou também levou
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </section>
      )}

      <ProductReviews productId={product.id} />

      {hasPrice && (
        <div ref={stickyCtaRef} className="md:hidden fixed inset-x-0 bottom-consent z-sticky-cta bg-white border-t border-champagne-dark px-4 pt-3 pb-safe flex items-center gap-3 shadow-[0_-8px_20px_-16px_rgba(60,36,21,0.3)]">
          {addedToCart ? (
            <div className="flex-1 min-w-0">{addedConfirmation}</div>
          ) : (
            <>
              <div className="shrink-0 leading-tight">
                {pixPrice !== null ? (
                  <>
                    <span className="block text-xl font-extrabold text-chocolate-dark">R$ {formatCurrency(pixPrice)}</span>
                    <span className="block text-xs text-text-medium">no Pix · R$ {formatCurrency(displayPrice as number)} no cartão</span>
                  </>
                ) : (
                  <>
                    {product.promotionalPrice && (
                      <span className="block text-xs text-text-medium line-through">R$ {formatCurrency(product.price as number)}</span>
                    )}
                    <span className="text-xl font-extrabold text-chocolate-dark">R$ {formatCurrency(displayPrice as number)}</span>
                  </>
                )}
              </div>
              <div className="flex-1 min-w-0">{purchaseButton}</div>
            </>
          )}
        </div>
      )}

      {lightboxOpen && mainImage && (
        <ProductLightbox
          images={product.images}
          currentUrl={mainImage}
          productName={product.name}
          onChange={setMainImage}
          onClose={closeLightbox}
        />
      )}

      <Modal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} title="Guia de tamanhos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-champagne-dark">
                <th className="py-2 pr-3 font-medium text-text-medium">Idade</th>
                <th className="py-2 pr-3 font-medium text-text-medium">Altura aproximada</th>
                <th className="py-2 font-medium text-text-medium">Peso aproximado</th>
              </tr>
            </thead>
            <tbody>
              {AGE_SIZE_GUIDE.map((row) => (
                <tr key={row.age} className="border-b border-champagne">
                  <td className="py-2 pr-3 text-text-dark font-medium whitespace-nowrap">{row.age}</td>
                  <td className="py-2 pr-3 text-text-medium whitespace-nowrap">{row.height}</td>
                  <td className="py-2 text-text-medium whitespace-nowrap">{row.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-text-light mt-4">
          Medidas aproximadas — podem variar conforme o modelo da peça. Em caso de dúvida, fale com a vendedora.
        </p>
      </Modal>
    </div>
  );
}
